import { useEffect, useMemo, useState } from "react";
import { loadTools, saveTools } from "@/services/db";
import { X, Plus, Trash, ToolCase, Rocket, Link2, CheckCircle2, AlertTriangle } from "lucide-react";
import type { MCPServer, MCPTransport, MCPTool } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "../ui/textarea";
import { MCPClient } from "@/services/mcpClient";
import { logger } from "react-native-logs";
import { ToolsToggle } from "@/components/ui/tools-toggle";
import { useLog } from "@/contexts/LogContext";

const loggerInstance = logger.createLogger();

interface ServerSettingsPanelProps {
  server: MCPServer | null;
  onClose: () => void;
  onSave: (server: MCPServer) => void;
  onDelete?: (server: MCPServer) => void;
}

type HeaderEntry = { key: string; value: string };

const createDefaultServer = (): MCPServer => ({
  id: crypto.randomUUID ? crypto.randomUUID() : `server-${Date.now()}`,
  name: "New MCP Server",
  url: "",
  transport: "http",
  status: "online",
  role: "",
});

export const ServerSettingsPanel = ({ server, onClose, onSave, onDelete }: ServerSettingsPanelProps) => {
  const mcpClient = useMemo(() => new MCPClient(), []);
  const { appendLog } = useLog();
  const [draft, setDraft] = useState<MCPServer | null>(server ?? createDefaultServer());
  const [headers, setHeaders] = useState<HeaderEntry[]>([]);
  const [savedServer, setSavedServer] = useState<MCPServer | null>(null);
  const [showConnectPrompt, setShowConnectPrompt] = useState(false);
  const [tools, setTools] = useState<MCPTool[]>([]);
  const [toolsLoaded, setToolsLoaded] = useState(false);
  const [showToolsList, setShowToolsList] = useState(false);

  // Helper to close both prompt and panel
  const handleClosePrompt = () => {
    setShowConnectPrompt(false);
    onClose();
  };
  const [connecting, setConnecting] = useState(false);
  const [connectResult, setConnectResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const next = server ?? createDefaultServer();
    setDraft(next);
    const list = next.headers
      ? Object.entries(next.headers).map(([key, value]) => ({ key, value }))
      : [{ key: "", value: "" }];
    setHeaders(list);
    setSavedServer(null);
    setShowConnectPrompt(false);
    setConnectResult(null);
    setTools([]);
    setToolsLoaded(false);
    setShowToolsList(false);
    // Load tools from DB if server exists
    if (next.id) {
      loadTools(next.id).then(loaded => {
        setTools(loaded);
        setToolsLoaded(true);
      });
    }
  }, [server]);
  // Lazy load tools for the server
  const handleToolsOpen = async () => {
    const id = savedServer?.id ?? server?.id;
    if (!id) return;
    if (!toolsLoaded) {
      const loaded = await loadTools(id);
      setTools(loaded);
      setToolsLoaded(true);
    }
    setShowToolsList(true);
  };

  interface ToolsChangeHandler {
    (next: MCPTool[]): Promise<void>;
  }

  const handleToolsChange: ToolsChangeHandler = async (next) => {
    setTools(next);
    const id: string | undefined = savedServer?.id ?? server?.id;
    if (id) await saveTools(id, next);
    setShowToolsList(false);
  };

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!draft) return null;

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleHeaderChange = (index: number, field: "key" | "value", value: string) => {
    setHeaders(prev => {
      const next = [...prev];
      const current = next[index] ?? { key: "", value: "" };
      next[index] = {
        key: field === "key" ? value : current.key,
        value: field === "value" ? value : current.value,
      };
      return next;
    });
  };

  const addHeader = () => setHeaders(prev => [...prev, { key: "", value: "" }]);
  const removeHeader = (index: number) => setHeaders(prev => prev.filter((_, i) => i !== index));

  const handleSave = () => {
    const sanitizedHeaders = headers.reduce<Record<string, string>>((acc, { key, value }) => {
      if (key.trim()) acc[key.trim()] = value;
      return acc;
    }, {});

    const payload: MCPServer = {
      ...draft,
      headers: sanitizedHeaders,
    };

    onSave(payload);
    setSavedServer(payload);
    setShowConnectPrompt(true);
    appendLog(`Server saved: ${payload.name} (${payload.url})`, "info");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
  };

  const handleDelete = () => {
    if (!draft) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (!draft) return;
    appendLog(`Server deleted: ${draft.name}`, "warn");
    onDelete?.(draft);
    // After deletion, close panel and switch to contacts view
    if (typeof window !== "undefined" && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent("mcp-switch-to-contacts"));
    }
    onClose();
    setShowDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const setValue = (field: keyof MCPServer, value: string | MCPTransport) =>
    setDraft(prev => (prev ? { ...prev, [field]: value } : prev));

  const initializeServer = (target: MCPServer) => mcpClient.initialize(target);

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-end bg-black/10 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="h-full w-full max-w-lg overflow-y-auto border-l border-border/60 bg-white/95 p-3 sm:p-6 shadow-2xl dark:bg-slate-900">
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-primary">Settings</p>
            <h3 className="text-base sm:text-xl font-semibold text-foreground truncate">{draft.name}</h3>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {server ? "Update the MCP server connection and auth." : "Create a new MCP server contact."}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close settings" className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="mt-3 sm:mt-6 space-y-3 sm:space-y-5">
          <div className="space-y-1 sm:space-y-2">
            <Label htmlFor="server-name" className="text-xs sm:text-sm">Display name</Label>
            <Input
              id="server-name"
              value={draft.name}
              onChange={e => setValue("name", e.target.value)}
              placeholder="Atlas"
              className="h-8 sm:h-9 text-xs sm:text-sm"
            />
          </div>

          <div className="space-y-1 sm:space-y-2">
            <Label htmlFor="server-url" className="text-xs sm:text-sm">MCP URL</Label>
            <Input
              id="server-url"
              value={draft.url}
              onChange={e => setValue("url", e.target.value)}
              placeholder="https://your-api.run.mcp.com.ai/mcp"
              className="h-8 sm:h-9 text-xs sm:text-sm"
            />
          </div>

          <div className="space-y-1 sm:space-y-2">
            <Label htmlFor="server-token" className="text-xs sm:text-sm">Auth token (Bearer)</Label>
            <Input
              id="server-token"
              type="password"
              value={draft.token ?? ""}
              onChange={e => setValue("token", e.target.value)}
              placeholder="sk-..."
              className="h-8 sm:h-9 text-xs sm:text-sm"
            />
          </div>

          <div className="grid grid-cols-1 gap-2 sm:gap-3 sm:grid-cols-2">
            <div className="space-y-1 sm:space-y-2">
              <Label className="text-xs sm:text-sm">Transport</Label>
              <Select
                value={draft.transport}
                onValueChange={value => setValue("transport", value as MCPTransport)}
              >
                <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm">
                  <SelectValue placeholder="Select transport" />
                </SelectTrigger>
                <SelectContent align="start">
                  {/* <SelectItem value="sse">SSE</SelectItem> */}
                  <SelectItem value="http">HTTP</SelectItem>
                  <SelectItem value="mock">Mock</SelectItem>
                </SelectContent>
              </Select>
              </div>
              {(connectResult?.success || tools.length > 0) && (
                <div className="space-y-1 sm:space-y-2" style={{ position: 'relative' }}>
                  <Label className="text-xs sm:text-sm">Enable/Disable Tools</Label>
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-1 sm:gap-2 h-8 sm:h-9 text-xs sm:text-sm w-full sm:w-auto"
                    onClick={handleToolsOpen}
                    style={{ position: 'relative', zIndex: 40 }}
                  >
                    <ToolCase className="h-3 w-3 sm:h-4 sm:w-4" aria-description="Tools" />
                    <span className="hidden sm:inline">Tools</span>
                    <span className="sm:hidden">T</span>
                    {tools.length ? ` (${tools.filter(t => t.enabled !== false).length}/${tools.length})` : ""}
                  </Button>
                  {showToolsList && (
                    <div
                      className="absolute w-64 z-50"
                      style={{ top: '-220px', left: '-200px' }} // Adjust height as needed for dropdown
                    >
                      <ToolsToggle
                        tools={tools}
                        onChange={handleToolsChange}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-1 sm:space-y-2">
              <Label htmlFor="server-role" className="text-xs sm:text-sm">Role</Label>
              <Textarea
                id="server-role"
                value={draft.role ?? ""}
                onChange={e => setValue("role", e.target.value)}
                placeholder="Describe the agent's role or purpose..."
                rows={3}
                className="min-h-[60px] sm:min-h-[80px] resize-none border-none bg-white/60 shadow-sm focus-visible:ring-2 focus-visible:ring-primary/30 dark:bg-white/5 text-xs sm:text-sm"
              />
            </div>

          <div className="space-y-1 sm:space-y-2">
            <Label className="text-xs sm:text-sm">Custom headers</Label>
            <div className="space-y-2 rounded-lg sm:rounded-xl border border-border/70 bg-white/70 p-2 sm:p-3 shadow-sm dark:bg-white/5">
              {headers.map((entry, index) => (
                <div key={`${entry.key}-${index}`} className="flex flex-col sm:grid sm:grid-cols-[1fr,1fr,auto] gap-1.5 sm:gap-2 sm:items-center">
                  <Input
                    value={entry.key}
                    onChange={e => handleHeaderChange(index, "key", e.target.value)}
                    placeholder="Header name"
                    className="h-8 sm:h-9 text-xs sm:text-sm"
                  />
                  <Input
                    value={entry.value}
                    onChange={e => handleHeaderChange(index, "value", e.target.value)}
                    placeholder="Header value"
                    className="h-8 sm:h-9 text-xs sm:text-sm"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={() => removeHeader(index)}
                    aria-label="Remove header"
                    className="h-7 w-7 sm:h-8 sm:w-8 self-end sm:self-center"
                  >
                    <Trash className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addHeader} className="gap-1 sm:gap-2 h-7 sm:h-8 text-xs w-full sm:w-auto">
                <Plus className="h-3 w-3 sm:h-4 sm:w-4" /> <span className="hidden sm:inline">Add header</span><span className="sm:hidden">Add</span>
              </Button>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
            {server ? (
              <Button variant="destructive" onClick={handleDelete} type="button" className="h-8 sm:h-9 text-xs sm:text-sm w-full sm:w-auto">
                Delete
              </Button>
            ) : null}
            {/* Delete confirmation dialog */}
            {showDeleteConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3 sm:px-4 backdrop-blur-sm">
                <div className="w-full max-w-sm rounded-xl sm:rounded-2xl border border-border/60 bg-white p-4 sm:p-6 shadow-2xl dark:bg-slate-900">
                  <h4 className="text-base sm:text-lg font-semibold text-foreground mb-2">Confirm Deletion</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">Are you sure you want to delete this server? This action cannot be undone.</p>
                  <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
                    <Button variant="ghost" onClick={cancelDelete} type="button" className="h-8 sm:h-9 text-xs sm:text-sm">Cancel</Button>
                    <Button variant="destructive" onClick={confirmDelete} type="button" className="h-8 sm:h-9 text-xs sm:text-sm">Delete</Button>
                  </div>
                </div>
              </div>
            )}
            <Button variant="ghost" onClick={onClose} type="button" className="h-8 sm:h-9 text-xs sm:text-sm w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" className="h-8 sm:h-9 text-xs sm:text-sm w-full sm:w-auto">
              Save changes
            </Button>
          </div>
        </form>
        <div className="flex items-start gap-2 rounded-lg sm:rounded-xl border border-white/50 bg-white/60 px-2 sm:px-3 py-2 text-[10px] sm:text-xs text-muted-foreground shadow-sm dark:border-white/10 dark:bg-white/5 mt-3 sm:mt-6">
          <Rocket className="h-4 w-4 sm:h-6 sm:w-6 text-cyan-500 flex-shrink-0 mt-0.5" />
          <span>
            Do you want to deploy your own MCP server directly from OpenAPI? Check out our{" "}
            <a
              href="https://run.mcp.com.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              MCP server deployment service
            </a>
            .
          </span>
        </div>

        {showConnectPrompt && savedServer ? (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-3 sm:px-4 backdrop-blur-sm">
            <div className="w-full max-w-lg rounded-xl sm:rounded-2xl border border-border/60 bg-white p-4 sm:p-6 shadow-2xl dark:bg-slate-900">
              <div className="flex items-start justify-between gap-2 sm:gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] sm:text-xs uppercase tracking-wide text-primary">Server saved</p>
                  <h4 className="text-base sm:text-lg font-semibold text-foreground truncate">{savedServer.name}</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    The server was added. Do you want to connect now and run MCP initialization?
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={handleClosePrompt} aria-label="Close" className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                  <X className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              </div>

              <div className="mt-3 sm:mt-4 space-y-2 sm:space-y-3 rounded-lg sm:rounded-xl border border-border/60 bg-white/70 p-3 sm:p-4 dark:bg-white/5">
                <div className="flex items-start gap-2 sm:gap-3">
                  <Link2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-semibold text-foreground break-all">{savedServer.url}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">
                      Transport: {savedServer.transport.toUpperCase()} • Status: {savedServer.status}
                    </p>
                  </div>
                </div>
                {connectResult ? (
                  <div
                    className={`flex items-start gap-1.5 sm:gap-2 rounded-lg px-2 sm:px-3 py-2 text-xs sm:text-sm ${
                      connectResult.success
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-rose-50 text-rose-700"
                    }`}
                  >
                    {connectResult.success ? (
                      <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="break-words">{connectResult.message}</span>
                  </div>
                ) : null}
              </div>

              <div className="mt-3 sm:mt-4 flex flex-col-reverse sm:flex-row justify-end gap-2">
                <Button variant={connectResult?.success ? "ghost" : "secondary"} onClick={handleClosePrompt} className="h-8 sm:h-9 text-xs sm:text-sm">
                  {connectResult?.success ? "Close" : "Maybe later"}
                </Button>
                {!connectResult?.success && (
                  <Button className="h-8 sm:h-9 text-xs sm:text-sm"
                    onClick={async () => {
                      if (!savedServer) return;
                      setConnecting(true);
                      setConnectResult(null);
                      try {
                        const result = await initializeServer(savedServer);
                        let toolsCount = 0;
                        try {
                          const tools = await mcpClient.listTools(savedServer);
                          toolsCount = tools.length;
                          await saveTools(savedServer.id, tools);
                        } catch (err: any) {
                          setConnectResult({
                            success: true,
                            message: `Connected: ${result?.serverInfo?.name ?? "MCP server"} (v${result?.serverInfo?.version ?? "unknown"}). Tool sync failed: ${err?.message ?? "Unknown error"}`,
                          });
                          setConnecting(false);
                          return;
                        }

                        setConnectResult({
                          success: true,
                          message: `Connected: ${result?.serverInfo?.name ?? "MCP server"} (v${result?.serverInfo?.version ?? "unknown"}). Synced ${toolsCount} tools.`,
                        });
                      } catch (error: any) {
                        setConnectResult({ success: false, message: error?.message ?? "Failed to connect" });
                      } finally {
                        setConnecting(false);
                      }
                    }}
                    disabled={connecting}
                  >
                    {connecting ? "Connecting..." : "Connect now"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
